// Every questionnaire answer as titled sections of question → answer rows, in the questionnaire's wording.
// The firm's Review and submit step and the staff Review (Firm's answers) both read it.
import { FUNCTIONS, OPTIONS, optionLabel, renewalLabel, type FunctionId, type IntakeAnswers } from '@upm/domain';
import { fmtCalendarDate } from '../lib/format';

/** `required`: needed to submit. `optional`: fine left blank, never counted as a question still to answer. */
export type AnswerRow = { k: string; v: string; required?: boolean; optional?: boolean };
export type AnswerSection = { title: string; step: number; rows: AnswerRow[] };

const r = (k: string, v: string | number | null | undefined, required = false): AnswerRow => ({ k, v: v === null || v === undefined ? '' : String(v), required });
const opt = (k: string, v: string | null | undefined): AnswerRow => ({ ...r(k, v), optional: true });
const join = (xs: Array<string | null | undefined>, sep: string) => xs.filter((x) => x && x.trim()).join(sep);
const labels = <K extends keyof typeof OPTIONS>(key: K, values: readonly string[] | undefined) =>
  (values ?? []).map((v) => optionLabel(key, v) ?? v).join(', ');

/**
 * `toolText`: a tools row's answer as text ("" when unanswered). `staff` adds the optional answers the firm's
 * summary leaves out: the website and where else documents live.
 */
export function answerSections(a: Partial<IntakeAnswers>, toolText: (fn: FunctionId) => string, opts: { staff?: boolean } = {}): AnswerSection[] {
  const b = a.breakdown;
  const parts = b ? [
    b.individual != null && `${b.individual} individuals`,
    b.business != null && `${b.business} businesses`,
    b.trust != null && `${b.trust} trusts and estates`,
    b.nonprofit != null && `${b.nonprofit} nonprofits`,
  ].filter(Boolean) as string[] : [];
  const noDocs = !!a.docsScope?.includes('none');
  const staff = (row: AnswerRow) => (opts.staff ? [row] : []);

  // `required` rows match validateForSubmit: step 1, every tool row, the active client count.
  return [
    { title: 'Your firm', step: 1, rows: [
      r('Firm', a.firmName, true),
      ...staff(opt('Firm website', a.website)),
      r('Primary contact', join([a.contact?.name, a.contact?.role], ', '), true),
      r('Email and phone', join([a.contact?.email, a.contact?.phone], ' · '), true),
      r('States', a.statesText, true),
      r('People at the firm', optionLabel('firmSize', a.firmSize), true),
      r('Logins needed', a.logins, true),
      r('Services', labels('services', a.services), true),
      // A timeframe, a specific date, or both: either one answers it.
      r('Timing', join([optionLabel('targetTiming', a.targetTiming), a.targetDate ? `Target date ${fmtCalendarDate(a.targetDate)}` : null], ' · '), true),
    ] },
    { title: 'Your tools', step: 2, rows: [
      ...FUNCTIONS.map((f) => r(f.label, toolText(f.id), true)),
      r('Tools that update each other', a.integrationsText),
      r('Spreadsheets and paper you rely on', a.fallbackText),
    ] },
    { title: 'Clients and work', step: 3, rows: [
      r('Active clients', a.clientsTotal != null ? `${a.clientsTotal}${parts.length ? ` (${parts.join(', ')})` : ''}` : '', true),
      r('Open returns or jobs', a.openJobs),
      r('Related clients grouped', optionLabel('groups', a.groups)),
      r('Checklists or templates', optionLabel('templates', a.templates)),
      r('Client list today', optionLabel('clientList', a.clientList)),
      r('Billing from tracked time', optionLabel('timeBilling', a.timeBilling)),
      r('Most renewals', renewalLabel(a.renewalMonth)),
    ] },
    { title: 'Documents', step: 4, rows: [
      r('Bring over', labels('docsScope', a.docsScope)),
      r('How far back', noDocs ? 'Not needed' : optionLabel('docsYears', a.docsYears)),
      r('Approximate size', noDocs ? 'Not needed' : optionLabel('docsSize', a.docsSize)),
      ...staff(opt('Anywhere else documents live', a.docsOtherLocation)),
      r('Work papers', 'Stay in your current storage'),
    ] },
    { title: 'Your questions', step: 5, rows: [
      opt('Most interested in', labels('interests', a.interests)),
      opt('Questions for us', a.questionsText),
      opt('Anything else before we meet', a.beforeMeetingText),
    ] },
  ];
}
